/**
 * Lightweight hook that subscribes only to themeMode for the current workspace.
 * Used by ThemeSync to apply workspace theme without loading full workspace settings.
 * Returns null until Firestore has responded, so we don't overwrite stored theme with "light" on first render.
 */

import { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import type { ThemeMode } from "../types";

export function useWorkspaceThemeMode(workspaceId: string | null): ThemeMode | null {
  const [themeMode, setThemeMode] = useState<ThemeMode | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setThemeMode(null);
      return;
    }

    const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
    const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const mode = data?.themeMode === "dark" ? "dark" : "light";
        setThemeMode(mode);
      } else {
        setThemeMode("light");
      }
    });

    return () => unsubscribe();
  }, [workspaceId]);

  return themeMode;
}
